import classNames from "classnames";
import { FunctionComponent, ReactNode, useEffect, useRef, useState } from "react";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import { ReactComponent as IconArrowLeft } from "@Icons/16x16/arrow-left.svg";
import { ReactComponent as IconArrowRight } from "@Icons/16x16/arrow-right.svg";
import "./style.scss";

export interface Props {
  children: ReactNode;
  displayArrows?: boolean;
}

const Carousel: FunctionComponent<Props> = ({ children, displayArrows = true }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [disabledLeft, setDisabledLeft] = useState<boolean>(true);
  const [disabledRight, setDisabledRight] = useState<boolean>(false);

  useEffect(() => {
    const element = scrollRef.current;
    if (element) {
      element.addEventListener("scroll", () => handleDisabled(element));
      return () => {
        element.removeEventListener("scroll", () => handleDisabled(element));
      };
    }
  }, [scrollRef.current]);

  const handleDisabled = (element: HTMLDivElement) => {
    setDisabledLeft(element.scrollLeft === 0);
    setDisabledRight(element.scrollLeft === element.scrollWidth - element.clientWidth);
  };

  const horizontalScroll = (element: HTMLDivElement, direction: string) => {
    const duration = 200;
    const scrollAmount = direction === "left" ? -350 : 350;
    const scrollUnit = scrollAmount / 100;
    let currentTime = 0;

    while (currentTime <= duration) {
      window.setTimeout(() => (element.scrollLeft += scrollUnit), currentTime);
      currentTime += duration / 100;
    }
  };

  const handleScroll = (direction: string): void => {
    if (scrollRef.current) {
      horizontalScroll(scrollRef.current, direction);
    }
  };

  if (displayArrows === false) {
    return (
      <div className="Carousel-wrapper">
        <div className="Carousel-list" ref={scrollRef}>
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="Carousel">
      <div
        className={classNames("Carousel-left", {
          "Carousel--disabled": disabledLeft,
        })}
      >
        <ButtonSmily
          disabled={disabledLeft}
          icon={<IconArrowLeft />}
          iconPosition="alone"
          onClick={() => handleScroll("left")}
          size="m"
          type="secondary"
        />
      </div>
      <div className="Carousel-wrapper">
        <div className="Carousel-list" ref={scrollRef}>
          {children}
        </div>
        <div
          className={classNames("Carousel-right", {
            "Carousel--disabled": disabledRight,
          })}
        >
          <ButtonSmily
            disabled={disabledRight}
            icon={<IconArrowRight />}
            iconPosition="alone"
            onClick={() => handleScroll("right")}
            size="m"
            type="secondary"
          />
        </div>
      </div>
    </div>
  );
};

export default Carousel;
